import React, { useState, useCallback } from 'react';
import DataTable from '../../components/common/DataTable';
import { usePagedData } from '../../hooks/usePagedData';
import { syncApi } from '../../api/endpoints';
import Select from '../../components/common/Select';
import { SkeletonTable } from '../../components/common/Skeleton';
import {
  FiSearch, FiRefreshCw, FiCheckCircle, FiXCircle, FiClock, FiLoader, FiActivity,
} from 'react-icons/fi';
import { toast } from 'react-toastify';

interface SyncLogItem {
  id: string;
  entityType: string;
  entityId: string | null;
  channel: string;
  operation: string;
  status: string;
  errorMessage: string | null;
  durationMs: number;
  createdAt: string;
}

const STATUS_MAP: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  Pending: { label: 'Oczekuje', color: '#94a3b8', icon: <FiClock /> },
  InProgress: { label: 'W toku', color: '#f59e0b', icon: <FiLoader /> },
  Completed: { label: 'Sukces', color: '#10b981', icon: <FiCheckCircle /> },
  Failed: { label: 'Błąd', color: '#ef4444', icon: <FiXCircle /> },
};

const ENTITY_TYPES = ['PurchaseOrder', 'Product', 'Invoice', 'Inventory', 'Shipment'];

const SyncLogsPage: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<SyncLogItem>(syncApi.logs, {
    initialSortBy: 'createdAt',
    initialSortDesc: true,
    initialPageSize: 50,
  });

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  }, [searchInput, setSearch]);

  const handleSyncAll = useCallback(async () => {
    setSyncing(true);
    try {
      await syncApi.syncAll();
      toast.success('Synchronizacja wszystkich danych rozpoczęta');
      setTimeout(() => refresh(), 2000);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd synchronizacji');
    } finally {
      setSyncing(false);
    }
  }, [refresh]);

  const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('pl-PL') + ' ' + date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  // Stats
  const items = data?.items || [];
  const totalCompleted = items.filter(i => i.status === 'Completed').length;
  const totalFailed = items.filter(i => i.status === 'Failed').length;
  const avgDuration = items.length > 0
    ? Math.round(items.reduce((s, i) => s + i.durationMs, 0) / items.length)
    : 0;

  const columns = [
    {
      key: 'createdAt',
      header: 'Data',
      sortable: true,
      render: (row: SyncLogItem) => (
        <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(row.createdAt)}</span>
      ),
    },
    {
      key: 'entityType',
      header: 'Typ',
      render: (row: SyncLogItem) => (
        <span className="badge badge-active" style={{ fontSize: '10px' }}>{row.entityType}</span>
      ),
    },
    {
      key: 'operation',
      header: 'Operacja',
      render: (row: SyncLogItem) => (
        <span style={{ fontSize: '12px', fontWeight: 500 }}>{row.operation}</span>
      ),
    },
    {
      key: 'channel',
      header: 'Kanał',
      render: (row: SyncLogItem) => (
        <span style={{ fontSize: '11px' }}>{row.channel === 'Vendor' ? '🏢 Vendor' : '🛒 Seller'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row: SyncLogItem) => {
        const s = STATUS_MAP[row.status] || STATUS_MAP.Pending;
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: s.color, fontWeight: 600, fontSize: '12px' }}>
            {s.icon} {s.label}
          </span>
        );
      },
    },
    {
      key: 'durationMs',
      header: 'Czas',
      sortable: true,
      render: (row: SyncLogItem) => (
        <span style={{
          fontSize: '11px',
          fontFamily: 'monospace',
          color: row.durationMs > 5000 ? '#ef4444' : row.durationMs > 2000 ? '#f59e0b' : 'var(--text-secondary)',
        }}>
          {formatDuration(row.durationMs)}
        </span>
      ),
    },
    {
      key: 'errorMessage',
      header: 'Szczegóły',
      render: (row: SyncLogItem) => {
        if (row.status === 'Failed' && row.errorMessage) {
          return (
            <span
              style={{ fontSize: '11px', color: '#ef4444', cursor: 'pointer', maxWidth: '200px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={row.errorMessage}
              onClick={(e) => { e.stopPropagation(); setExpandedRow(expandedRow === row.id ? null : row.id); }}
            >
              ⚠️ {row.errorMessage}
            </span>
          );
        }
        if (row.entityId) {
          return <span style={{ fontSize: '10px', fontFamily: 'monospace', color: 'var(--text-muted)' }}>{row.entityId.substring(0, 8)}...</span>;
        }
        return <span style={{ color: 'var(--text-muted)' }}>—</span>;
      },
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Logi synchronizacji</h1>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleSyncAll} disabled={syncing}>
            <FiRefreshCw className={syncing ? 'spin' : ''} />
            {syncing ? 'Synchronizuję...' : 'Synchronizuj wszystko'}
          </button>
        </div>
      </div>

      {/* Mini stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
        <div className="card">
          <div className="card-body" style={{ padding: '12px', textAlign: 'center' }}>
            <FiActivity style={{ color: '#6366f1', fontSize: '18px' }} />
            <div style={{ fontSize: '20px', fontWeight: 800 }}>{data?.totalCount || 0}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Łącznie</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: '12px', textAlign: 'center' }}>
            <FiCheckCircle style={{ color: '#10b981', fontSize: '18px' }} />
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#10b981' }}>{totalCompleted}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Sukces (strona)</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: '12px', textAlign: 'center' }}>
            <FiXCircle style={{ color: '#ef4444', fontSize: '18px' }} />
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#ef4444' }}>{totalFailed}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Błędy (strona)</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: '12px', textAlign: 'center' }}>
            <FiClock style={{ color: '#f59e0b', fontSize: '18px' }} />
            <div style={{ fontSize: '20px', fontWeight: 800 }}>{formatDuration(avgDuration)}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Śr. czas</div>
          </div>
        </div>
      </div>

      {/* Expanded error detail */}
      {expandedRow && (() => {
        const row = items.find(i => i.id === expandedRow);
        if (!row || !row.errorMessage) return null;
        return (
          <div className="bulk-actions-bar" style={{ background: '#fef2f2', borderColor: '#ef4444', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: '4px' }}>Szczegóły błędu — {row.operation} ({row.entityType})</div>
              <pre style={{ margin: 0, fontSize: '11px', whiteSpace: 'pre-wrap', color: '#991b1b', fontFamily: 'monospace', maxHeight: '150px', overflow: 'auto' }}>
                {row.errorMessage}
              </pre>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => setExpandedRow(null)}>✕</button>
          </div>
        );
      })()}

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Szukaj operacji..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary"><FiSearch /></button>
          </div>
        </form>
        <div className="toolbar-filters" style={{ display: 'flex', gap: '0.5rem' }}>
          <Select
            options={[
              { value: '', label: 'Wszystkie statusy' },
              { value: 'Completed', label: '✅ Sukces' },
              { value: 'Failed', label: '❌ Błąd' },
              { value: 'Pending', label: '⏳ Oczekuje' },
              { value: 'InProgress', label: '🔄 W toku' },
            ]}
            value={String(filters.status ?? '')}
            onChange={(v) => setFilters((p: any) => ({ ...p, status: v || undefined }))}
            placeholder="Wszystkie statusy"
          />
          <Select
            options={[
              { value: '', label: 'Wszystkie typy' },
              ...ENTITY_TYPES.map(t => ({ value: t, label: t })),
            ]}
            value={String(filters.entityType ?? '')}
            onChange={(v) => setFilters((p: any) => ({ ...p, entityType: v || undefined }))}
            placeholder="Wszystkie typy"
          />
        </div>
      </div>

      {loading ? (
        <SkeletonTable rows={12} cols={7} />
      ) : (
        <DataTable
          columns={columns}
          data={items}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
        />
      )}
    </div>
  );
};

export default SyncLogsPage;
